import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { Chip, Stack } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { theme } from '../../../ThemeContext/ThemeObject';
import { classes } from './styles';
import { ISelectedApplicantsProps } from '../types';

// Interface for ColorCodedTagList props
interface ColorCodedTagListProps {
  originalTags: string[];
  modifiedTags: string[];
  removedTags?: string[];
}

// Custom component to display tags with color coding for added and removed tags
const ColorCodedTagList: React.FC<ColorCodedTagListProps> = ({
  originalTags,
  modifiedTags,
  removedTags = []
}) => {
  // Identify added tags
  const addedTags = modifiedTags.filter((tag) => !originalTags.includes(tag));
  // Only consider a tag as removed if it's in the removedTags array
  const actuallyRemovedTags = originalTags.filter((tag) => removedTags.includes(tag));

  // Function to render a single tag with appropriate styling
  const renderTag = (tag: string, status: 'added' | 'removed' | 'original') => {
    let chipStyle = {};
    let label = tag;

    if (status === 'added') {
      chipStyle = { backgroundColor: '#e6f7e6', color: '#2e7d32' };
      label = `${tag}`;
    } else if (status === 'removed') {
      chipStyle = { backgroundColor: '#ffebee', color: '#d32f2f' };
      label = `${tag}`;
    } else {
      chipStyle = { backgroundColor: '#F3F3F3', color: '#333333' };
    }

    return <Chip key={`${tag}-${status}`} label={label} style={{ margin: '2px', ...chipStyle }} />;
  };

  return (
    <Stack direction="row" flexWrap="wrap">
      {/* Show all original tags that are not removed with default styling */}
      {originalTags
        .filter((tag) => !actuallyRemovedTags.includes(tag))
        .map((tag) => renderTag(tag, 'original'))}
      {/* Show all removed tags with red styling and "(Removed)" label */}
      {actuallyRemovedTags.map((tag) => renderTag(tag, 'removed'))}
      {/* Show all added tags with green styling and "(Added)" label */}
      {addedTags.map((tag) => renderTag(tag, 'added'))}
    </Stack>
  );
};

export default function SelectedApplicantsWithTags({
  globalSelected,
  handleDeselectCandidate,
  closeModalCallback,
  modifiedTags,
  removedTags
}: ISelectedApplicantsProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const filterCandidates = (application: ISelectedApplicantsProps['globalSelected'][0]) => {
    return searchTerm
      ? `${application.candidate?.firstname?.toLowerCase()} ${application.candidate?.lastname?.toLowerCase()}`.includes(
          searchTerm.toLowerCase()
        ) || application.candidate?.phone1?.includes(searchTerm)
      : true;
  };

  const onRemoveCandidate = (id: number) => {
    handleDeselectCandidate(id);
    if (globalSelected?.length <= 1) closeModalCallback();
  };

  return (
    <>
      <OutlinedInput
        id="job-search"
        sx={classes.candidateSearch}
        placeholder={'Search candidates'}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        startAdornment={
          <InputAdornment sx={{ padding: '0px 0px 0px 10px' }} position="start">
            <SearchIcon />
          </InputAdornment>
        }
      />
      <Box sx={classes.selectedApplicantsTableHeader}>
        {globalSelected.length} candidates selected
      </Box>
      <TableContainer sx={{ boxShadow: 'none' }} component={Paper}>
        <Table
          sx={{ ...classes.selectedApplicantsTable, width: 'unset' }}
          aria-label="selected applicants"
        >
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {globalSelected
              .filter((candidate) => filterCandidates(candidate))
              .map((application: ISelectedApplicantsProps['globalSelected'][0]) => (
                <TableRow key={application.id}>
                  <TableCell>
                    {application.candidate.firstname} {application.candidate.lastname}
                  </TableCell>
                  <TableCell>{application.candidate.email}</TableCell>
                  <TableCell sx={{ maxWidth: '200px' }}>
                    <ColorCodedTagList
                      originalTags={application.candidate.tag_list}
                      modifiedTags={modifiedTags || []}
                      removedTags={removedTags}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => onRemoveCandidate(application.id)}
                      sx={{ marginLeft: '35px' }}
                    >
                      <ClearIcon sx={{ color: theme.palette.error.main }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
